import Mustache from 'mustache';

/**
 * Generic mustache-template walking, shared by webhook payload and header
 * template validation.
 *
 * Section resolution (how a `{{#section}}`'s children should be prefixed when
 * checking their names against an allow-list) is caller-specific — payload
 * templates resolve against a schema-derived path tree, header templates have
 * no sections at all — so it's injected as an optional resolver instead of
 * being baked into the walker.
 */

export type MustacheToken = [string, string, number, number, MustacheToken[]?];

export const SKIP_SYMBOLS: ReadonlySet<string> = new Set(['text', '!', '/']);
export const SECTION_SYMBOLS: ReadonlySet<string> = new Set(['#', '^']);
export const VARIABLE_SYMBOLS: ReadonlySet<string> = new Set([
  'name',
  '&',
  '#',
  '^',
]);

/**
 * Resolve a section name to the prefix its children should be walked under, or
 * null when the section is unknown to the caller. Callers with no notion of
 * nested, schema-derived variables (e.g. header templates) can omit this,
 * which keeps the walk flat.
 */
export type SectionPrefixResolver = (
  name: string,
  prefix: string,
) => string | null;

/** Parse a template with mustache; throws on bad syntax. */
export const parseTemplate = (template: string): MustacheToken[] =>
  Mustache.parse(template) as MustacheToken[];

// Visit every token depth-first, tracking the resolved section-prefix so nested
// (section-relative) names can be resolved against their full path.
export function walkTemplate(
  tokens: MustacheToken[],
  prefix: string,
  visit: (symbol: string, name: string, prefix: string) => void,
  resolveSectionPrefix?: SectionPrefixResolver,
): void {
  for (const [symbol, name, , , subTokens] of tokens) {
    visit(symbol, name, prefix);
    if (SECTION_SYMBOLS.has(symbol)) {
      const resolved = resolveSectionPrefix?.(name, prefix) ?? null;
      const childPrefix = resolved && resolved !== '.' ? resolved : prefix;
      walkTemplate(subTokens ?? [], childPrefix, visit, resolveSectionPrefix);
    }
  }
}

/** Parse a template and return every distinct variable it references. */
export function extractTemplateVariables(
  template: string,
  resolveSectionPrefix?: SectionPrefixResolver,
): string[] {
  const found = new Set<string>();
  walkTemplate(
    parseTemplate(template),
    '',
    (symbol, name, prefix) => {
      if (VARIABLE_SYMBOLS.has(symbol) && name !== '.') {
        found.add(prefix ? `${prefix}.${name}` : name);
      }
    },
    resolveSectionPrefix,
  );
  return [...found];
}
